import type { StudioContextLike } from './handlers/store.js';

// Adapter that bridges aiaudio-extensions to openDAW's real project model.
// This file is the AGPL boundary point: it consumes a live openDAW Project
// structurally (no `@opendaw/*` imports — types mirror the runtime shape) and
// translates to the bridge's `StudioContextLike` shape.
//
// Two factory entry points are exposed:
//
//   1. `createOpenDawAdapter({ project, createAudioTrack? })` (INT03 path)
//      — used by drop-zone / import code that already has a project handle
//      and an injected `createAudioTrack`. Backwards compatible.
//
//   2. `makeStudioContext(studio, options?)` — wraps openDAW's runtime studio
//      singleton or the live `Project` itself. The 5 channel-strip mutators
//      (volume/mute/solo/pan/name) and `addMarker` run natively against
//      `rootBoxAdapter.audioUnits.adapters()` / `timelineBoxAdapter.markerTrack`
//      inside `editing.modify(...)`. `addAudioTrack` requires an injected
//      creator (`addAudioTrackDirect`) because it constructs new `AudioFileBox`
//      / `TrackBox` / `AudioRegionBox` instances — openDAW symbols not
//      accessible via duck-typing.

export interface OpenDawAdapterOptions {
  // The openDAW project handle. Typed as `unknown` because this file does not
  // depend on `@opendaw/studio-core`; at runtime it's a real `Project`.
  project: unknown;
  // Optional injection point so a host can supply the real openDAW
  // track-creation function without this file taking a hard dependency on
  // `daw/packages/*`. The host wires this from inside the studio bundle.
  createAudioTrack?: (info: {
    name: string;
    audio_blob_hash: string;
    duration_seconds: number;
    sample_rate: number;
    channels: number;
  }) => Promise<{ track_id: string; region_id: string }>;
}

export function createOpenDawAdapter(opts: OpenDawAdapterOptions): StudioContextLike {
  return {
    getProject(): unknown {
      return opts.project;
    },
    async addAudioTrack(info): Promise<{ track_id: string; region_id: string }> {
      if (opts.createAudioTrack) {
        return opts.createAudioTrack(info);
      }
      // No injection: persist still happened upstream (OPFS via writeAudio),
      // but the project hasn't materialised a track. Surface clearly.
      console.warn(
        `openDawAdapter.addAudioTrack: no createAudioTrack injected — audio ${info.audio_blob_hash} (${info.name}) ` +
          'persisted to OPFS but not added to project.',
      );
      return {
        track_id: `placeholder-track-${info.audio_blob_hash.slice(0, 8)}`,
        region_id: `placeholder-region-${info.audio_blob_hash.slice(0, 8)}`,
      };
    },
  };
}

// -------------------------------------------------------------------------
// `makeStudioContext` — real openDAW project mutations.
// -------------------------------------------------------------------------
//
// Structural typing against openDAW's runtime shape (see comments below for
// the cited source files). Runtime: the studio entry passes either the
// `StudioService` singleton (with `.project` / `.getProject()`) or the live
// `Project` directly.

// --- Minimal structural shapes the adapter touches at runtime. ---
// These mirror (but do not import from) openDAW; cited source files in
// comments verify field names. Erased at compile time — runtime is unchanged.

interface ParameterLike {
  setValue: (value: number | boolean) => void;
}

// AudioUnitBoxAdapter#namedParameter -- see
// daw/packages/studio/adapters/src/audio-unit/AudioUnitBoxAdapter.ts L122-140
interface NamedAudioUnitParams {
  volume: ParameterLike;
  panning: ParameterLike;
  mute: ParameterLike;
  solo: ParameterLike;
}

interface LabelField {
  setValue: (value: string) => void;
  getValue?: () => string;
}

interface AudioUnitInputAdapter {
  labelField: LabelField;
}

interface OptionLike<T> {
  unwrap: () => T;
  unwrapOrUndefined?: () => T | undefined;
  nonEmpty?: () => boolean;
  isEmpty?: () => boolean;
}

interface AudioUnitInput {
  adapter: () => OptionLike<AudioUnitInputAdapter>;
}

// AudioUnitBoxAdapter — `uuid` is `UUID.Bytes` (Uint8Array length 16). See
// daw/packages/studio/adapters/src/audio-unit/AudioUnitBoxAdapter.ts L55
interface AudioUnitAdapterLike {
  uuid: Uint8Array;
  input: AudioUnitInput;
  namedParameter: NamedAudioUnitParams;
}

// RootBoxAdapter.audioUnits — see
// daw/packages/studio/adapters/src/RootBoxAdapter.ts L42
interface IndexedAdapterCollection<T> {
  adapters: () => ReadonlyArray<T>;
}

interface RootBoxAdapterLike {
  audioUnits: IndexedAdapterCollection<AudioUnitAdapterLike>;
}

// Editing#modify — see Project.ts L163 / Mixer/RecordAudio usage.
interface EditingLike {
  modify: (fn: () => void, ...rest: unknown[]) => void;
}

// timelineBox.bpm -- see TimelineBoxAdapter / ProjectApi.setBpm (L106).
// We read bpm from `timelineBoxAdapter.box.bpm.getValue()`.
interface PrimitiveValueField<T> {
  getValue: () => T;
}

interface TimelineBoxLike {
  bpm: PrimitiveValueField<number>;
}

interface TimelineBoxAdapterLike {
  box: TimelineBoxLike;
}

// Project — see daw/packages/studio/core/src/project/Project.ts L82+
interface ProjectLike {
  editing: EditingLike;
  rootBoxAdapter: RootBoxAdapterLike;
  timelineBoxAdapter: TimelineBoxAdapterLike;
}

export interface MakeStudioContextOptions {
  // Host can override the BPM lookup if the studio exposes a richer transport.
  getBpm?: () => number;
  // Injected MarkerBox creator. The handler computes PPQN position from
  // seconds via the project's BPM (PPQN.Quarter = 960 — see
  // daw/packages/lib/dsp/src/ppqn.ts L11). The host must run this inside
  // `editing.modify(...)` and return the resulting marker's id/name/seconds.
  addMarkerDirect?: (input: {
    name: string;
    position_ppqn: number;
    position_seconds: number;
    hue?: number;
    color?: string;
  }) => { id: string; name: string; position_seconds: number; color?: string };
  // Injected audio-track creator (resolves OPFS blob, builds AudioFileBox +
  // TrackBox + AudioRegionBox). Must wrap in `editing.modify(...)`.
  addAudioTrackDirect?: (info: {
    name: string;
    audio_blob_hash: string;
    duration_seconds: number;
    sample_rate: number;
    channels: number;
  }) => Promise<{ track_id: string; region_id: string }>;
}

// `studio` may be a static handle OR a thunk. The thunk form lets callers
// re-resolve the live project on each call (e.g. after the user opens a new
// project, the StudioService's `.project` accessor returns a different
// instance — capturing once at construction would go stale).
export type StudioRef = unknown | (() => unknown);

function resolveProjectRef(ref: StudioRef): ProjectLike | null {
  const studio = typeof ref === 'function' ? (ref as () => unknown)() : ref;
  return resolveProject(studio);
}

export function makeStudioContext(
  studio: StudioRef,
  options: MakeStudioContextOptions = {},
): StudioContextLike {
  const findAudioUnit = (track_id: string): AudioUnitAdapterLike | null => {
    const project = resolveProjectRef(studio);
    if (project === null) return null;
    for (const audioUnit of project.rootBoxAdapter.audioUnits.adapters()) {
      if (uuidToString(audioUnit.uuid) === track_id) return audioUnit;
    }
    return null;
  };
  const inEditing = (fn: () => void): void => {
    const project = resolveProjectRef(studio);
    if (project === null) {
      fn();
      return;
    }
    project.editing.modify(fn);
  };
  const ctx: StudioContextLike = {
    getProject: () => resolveProjectRef(studio),
    setTrackVolume: (track_id, db) => {
      const audioUnit = findAudioUnit(track_id);
      if (audioUnit === null) throw new Error(`TRACK_NOT_FOUND: ${track_id}`);
      inEditing(() => audioUnit.namedParameter.volume.setValue(db));
    },
    setTrackMute: (track_id, mute) => {
      const audioUnit = findAudioUnit(track_id);
      if (audioUnit === null) throw new Error(`TRACK_NOT_FOUND: ${track_id}`);
      inEditing(() => audioUnit.namedParameter.mute.setValue(mute));
    },
    setTrackSolo: (track_id, solo) => {
      const audioUnit = findAudioUnit(track_id);
      if (audioUnit === null) throw new Error(`TRACK_NOT_FOUND: ${track_id}`);
      inEditing(() => audioUnit.namedParameter.solo.setValue(solo));
    },
    setTrackPan: (track_id, pan) => {
      const audioUnit = findAudioUnit(track_id);
      if (audioUnit === null) throw new Error(`TRACK_NOT_FOUND: ${track_id}`);
      inEditing(() => audioUnit.namedParameter.panning.setValue(pan));
    },
    setTrackName: (track_id, name) => {
      const audioUnit = findAudioUnit(track_id);
      if (audioUnit === null) throw new Error(`TRACK_NOT_FOUND: ${track_id}`);
      const optInput = audioUnit.input.adapter();
      const inputAdapter = optInput.unwrap();
      inEditing(() => inputAdapter.labelField.setValue(name));
    },
    addMarker: (input) => {
      const project = resolveProjectRef(studio);
      const bpm = currentBpm(project, options);
      const position_ppqn = secondsToPpqn(input.position_seconds, bpm);
      const hue = colorToHue(input.color);
      if (typeof options.addMarkerDirect === 'function') {
        return options.addMarkerDirect({
          name: input.name,
          position_ppqn,
          position_seconds: input.position_seconds,
          ...(hue !== undefined ? { hue } : {}),
          ...(typeof input.color === 'string' ? { color: input.color } : {}),
        });
      }
      console.warn(
        'openDawAdapter.addMarker: no addMarkerDirect injected — marker not persisted to project.',
      );
      return {
        id: `marker-pending-${Math.random().toString(36).slice(2, 10)}`,
        name: input.name,
        position_seconds: input.position_seconds,
        ...(typeof input.color === 'string' ? { color: input.color } : {}),
      };
    },
  };
  if (typeof options.addAudioTrackDirect === 'function') {
    const directFn = options.addAudioTrackDirect;
    ctx.addAudioTrack = (info) => directFn(info);
  }
  return ctx;
}

// Resolve a runtime project handle from any of: the project itself, a studio
// service exposing `.project`, or one exposing `.getProject()`.
function resolveProject(studio: unknown): ProjectLike | null {
  if (!isObject(studio)) return null;
  if (isProjectLike(studio)) return studio as unknown as ProjectLike;
  const direct = studio['project'];
  if (isObject(direct) && isProjectLike(direct)) return direct as unknown as ProjectLike;
  const getter = studio['getProject'];
  if (typeof getter === 'function') {
    const value = (getter as () => unknown).call(studio);
    if (isObject(value) && isProjectLike(value)) return value as unknown as ProjectLike;
  }
  return null;
}

function isProjectLike(value: Record<string, unknown>): boolean {
  return (
    isObject(value['editing']) &&
    typeof (value['editing'] as Record<string, unknown>)['modify'] === 'function' &&
    isObject(value['rootBoxAdapter'])
  );
}

function currentBpm(project: ProjectLike | null, options: MakeStudioContextOptions): number {
  if (typeof options.getBpm === 'function') return options.getBpm();
  if (project !== null) {
    const value = project.timelineBoxAdapter?.box?.bpm?.getValue?.();
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }
  return 120;
}

// PPQN.Quarter = 960 — see daw/packages/lib/dsp/src/ppqn.ts L11.
// PPQN.secondsToPulses(seconds, bpm) = seconds * bpm / 60 * 960.
const PPQN_QUARTER = 960;

function secondsToPpqn(seconds: number, bpm: number): number {
  return Math.round((seconds * bpm / 60.0) * PPQN_QUARTER);
}

// MarkerBox.hue is an int (`HueConstraints`, see MarkerBox schema). Convert a
// `#rrggbb` hex string to an HSL hue in [0, 360). Returns undefined for
// non-hex inputs so the caller can default.
function colorToHue(color: string | undefined): number | undefined {
  if (typeof color !== 'string') return undefined;
  const match = /^#?([0-9a-fA-F]{6})$/.exec(color.trim());
  if (match === null) return undefined;
  const hex = match[1];
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  if (delta === 0) return 0;
  let h: number;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h *= 60;
  if (h < 0) h += 360;
  return Math.round(h);
}

// Inlined `UUID.toString` — see daw/packages/lib/std/src/uuid.ts L41-50.
// Avoids a hard import on `@opendaw/lib-std` from this boundary file.
const HEX_TABLE: ReadonlyArray<string> = (() => {
  const out: string[] = [];
  for (let i = 0; i < 256; i++) out.push((i + 0x100).toString(16).slice(1));
  return out;
})();

function uuidToString(bytes: Uint8Array): string {
  if (!(bytes instanceof Uint8Array) || bytes.length !== 16) {
    // Fall back to a best-effort hex dump for any unexpected shape so the
    // caller still gets a stable string id.
    return Array.from(bytes ?? []).map((byte) => (byte + 0x100).toString(16).slice(1)).join('');
  }
  return (
    HEX_TABLE[bytes[0]] + HEX_TABLE[bytes[1]] + HEX_TABLE[bytes[2]] + HEX_TABLE[bytes[3]] + '-' +
    HEX_TABLE[bytes[4]] + HEX_TABLE[bytes[5]] + '-' +
    HEX_TABLE[bytes[6]] + HEX_TABLE[bytes[7]] + '-' +
    HEX_TABLE[bytes[8]] + HEX_TABLE[bytes[9]] + '-' +
    HEX_TABLE[bytes[10]] + HEX_TABLE[bytes[11]] +
    HEX_TABLE[bytes[12]] + HEX_TABLE[bytes[13]] +
    HEX_TABLE[bytes[14]] + HEX_TABLE[bytes[15]]
  );
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
